/**
 * AccountSection — Innstillinger → Konto (#141).
 *
 *   - viser brukernavnet (GET /api/auth/me → member.username)
 *   - Bytt passord      POST /api/auth/password   (serveren logger ut andre økter)
 *   - PIN for kjøkkenskjermen  PUT /api/auth/pin-code (krever passordet)
 *   - Logg ut           POST /api/auth/logout → tøm cachen → /login
 *
 * Skjemaene er lukket til man åpner dem, og passordene ligger bare i lokal
 * state mens skjemaet er åpent; de tømmes ved lagring og ved Avbryt.
 *
 * `ChangePasswordForm` og `LogoutButton` brukes også av /velg-passord (#143),
 * der passordskjemaet står åpent og ikke kan avbrytes.
 *
 * #159 (steg 9d, Stell_Meg): Meg setter delene sammen selv. `ProfileCard` er
 * profilkortet øverst (den kosmetiske avataren, D2), `AccountSection` er
 * kortet med radene Bytt passord og PIN for kjøkkenskjermen, som åpner
 * skjemaene i et ark, og `LogoutButton variant="link"` er «Logg ut» nederst.
 */
import { useMemo, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PinSetBody } from '@stell/shared';
import { BottomSheet, Button, Icon, asColorSlot } from '../../components/primitives';
import type { ApiError } from '../../lib/api';
import {
  AuthApi,
  asApiError,
  authErrorMessage,
  splitAuthError,
  type AuthFieldErrors,
} from '../auth/authApi';
import {
  EMPTY_NEW_PASSWORD,
  HiddenUsername,
  NewPasswordFields,
  PasswordField,
  PinInput,
  isNewPasswordValid,
  type NewPasswordValue,
} from '../auth/AuthFields';
import { useResetMemberCache } from '../auth/sessionCache';
import { useSession } from '../auth/useSession';
import { ADMIN_CARD, ADMIN_LIST, ADMIN_ROW } from '../admin/adminStyles';
import { CosmeticAvatar, equippedLayers } from '../shop/CosmeticAvatar';
import { useOwnedCosmetics } from '../shop/useCosmetics';

/** Profilkortet: avatar, navn, rolle og brukernavn. */
export function ProfileCard(): ReactElement {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const member = session?.member;
  const username = member?.username ?? null;
  const owned = useOwnedCosmetics(member?.id);
  const equipped = useMemo(() => equippedLayers(owned.data ?? []), [owned.data]);
  return (
    <section data-testid="account-profile" className={`${ADMIN_CARD} flex items-center gap-3.5`}>
      <CosmeticAvatar
        colorSlot={asColorSlot(member?.color_token)}
        equipped={equipped}
        size={56}
        className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-heading-s font-extrabold text-ink">{member?.name ?? ''}</p>
        {member ? (
          <p className="m-0 text-control text-ink-2">
            {t(ROLE_KEY[member.role])}
            {username ? (
              <>
                {' · '}
                <span data-testid="account-username" className="font-mono">
                  {username}
                </span>
              </>
            ) : null}
          </p>
        ) : null}
        {member && !username ? (
          <p data-testid="account-username" className="m-0 text-caption text-ink-2">
            {t('account.no_username')}
          </p>
        ) : null}
      </div>
    </section>
  );
}

const ROLE_KEY = {
  admin: 'onboarding.step3.role_admin',
  teen: 'onboarding.step3.role_teen',
  child: 'onboarding.step3.role_child',
} as const;

/** Kortet med radene Bytt passord og PIN for kjøkkenskjermen. */
export function AccountSection(): ReactElement {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const username = session?.member?.username ?? null;
  return (
    <section
      data-testid="account-section"
      aria-label={t('account.section')}
      className={`${ADMIN_LIST} divide-y divide-card-line`}
    >
      <ChangePasswordForm username={username} />
      <KitchenPinForm username={username} />
    </section>
  );
}

// ─── Felles: et skjema bak en rad ───────────────────────────────────────────

interface DisclosureFormProps {
  title: string;
  body?: string;
  /** Raden i Meg som åpner arket. */
  rowLabel: string;
  submitLabel: string;
  doneMessage: string;
  testId: string;
  /** For passordbehandlere: skjult brukernavn i skjemaet. */
  username: string | null;
  canSubmit: boolean;
  /** Lagrer; kaster `ApiError` ved feil. */
  onSubmit: () => Promise<unknown>;
  /** Tømmer feltene — etter lagring og ved Avbryt. */
  onReset: () => void;
  onError?: (err: ApiError) => void;
  /** Feltene; får serverfeilen fordelt på feltene den hører til. */
  children: (errors: AuthFieldErrors) => ReactNode;
  /** Skjemaet står åpent og kan ikke avbrytes (#143, /velg-passord). */
  alwaysOpen?: boolean;
  /** Etter vellykket lagring. */
  onDone?: () => void;
}

/**
 * Eier åpen/lagrer/feil/kvittering, så hvert skjema bare har feltene sine.
 * I Meg er skjemaet en rad som åpner det i et ark (#159, steg 9d); med
 * `alwaysOpen` står det åpent på siden, som på /velg-passord.
 */
function DisclosureForm({
  title,
  body,
  rowLabel,
  submitLabel,
  doneMessage,
  testId,
  username,
  canSubmit,
  onSubmit,
  onReset,
  onError,
  children,
  alwaysOpen = false,
  onDone,
}: DisclosureFormProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetMemberCache = useResetMemberCache();
  const [open, setOpen] = useState(alwaysOpen);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState(false);
  const errors = splitAuthError(t, error);

  function close(): void {
    onReset();
    setError(null);
    setOpen(alwaysOpen);
  }

  async function submit(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await onSubmit();
      close();
      setDone(true);
      onDone?.();
    } catch (err) {
      const e = asApiError(err);
      // Økten ble tilbakekalt mens skjemaet var åpent (f.eks. passordbytte på
      // en annen enhet): da er vi logget ut, som etter Logg ut.
      if (e.code === 'unauthenticated') {
        resetMemberCache('logged_out');
        navigate('/login', { replace: true });
        return;
      }
      setError(e);
      onError?.(e);
    } finally {
      setPending(false);
    }
  }

  const form = (
    <form
      data-testid={`${testId}-form`}
      className="flex max-w-sm flex-col gap-4"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (canSubmit && !pending) void submit();
      }}
    >
      <HiddenUsername value={username} />
      {children(errors)}
      {errors.other ? (
        <p data-testid={`${testId}-error`} role="alert" className="text-bad text-sm">
          {errors.other}
        </p>
      ) : null}
      <div className="flex justify-end gap-3">
        {alwaysOpen ? null : (
          <Button variant="ghost" onClick={close} data-testid={`${testId}-cancel`}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" disabled={!canSubmit || pending} data-testid={`${testId}-submit`}>
          {pending ? t('account.saving') : submitLabel}
        </Button>
      </div>
    </form>
  );

  const doneNote = done ? (
    <p data-testid={`${testId}-done`} role="status" className="text-ink-2 text-sm">
      {doneMessage}
    </p>
  ) : null;

  if (alwaysOpen) {
    return (
      <div className="border-card-line bg-raised space-y-3 rounded-lg border p-4">
        <h3 className="text-ink font-medium">{title}</h3>
        {body ? <p className="text-ink-2 max-w-prose text-sm">{body}</p> : null}
        {doneNote}
        {form}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={ADMIN_ROW}
        onClick={() => {
          setDone(false);
          setOpen(true);
        }}
        data-testid={`${testId}-open`}
      >
        <span className="min-w-0 flex-1">{rowLabel}</span>
        <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
      </button>
      {doneNote ? <div className="pb-3">{doneNote}</div> : null}
      <BottomSheet
        open={open}
        onClose={() => {
          if (!pending) close();
        }}
        title={title}
      >
        {body ? <p className="mb-4 max-w-prose text-sm text-ink-2">{body}</p> : null}
        {form}
      </BottomSheet>
    </div>
  );
}

// ─── Bytt passord ───────────────────────────────────────────────────────────

export function ChangePasswordForm({
  username,
  alwaysOpen,
  onDone,
  submitLabel,
  currentLabel,
}: {
  username: string | null;
  alwaysOpen?: boolean;
  onDone?: () => void;
  submitLabel?: string;
  currentLabel?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState<NewPasswordValue>(EMPTY_NEW_PASSWORD);

  return (
    <DisclosureForm
      title={t('account.password.title')}
      rowLabel={t('account.password.open')}
      submitLabel={submitLabel ?? t('account.password.submit')}
      alwaysOpen={alwaysOpen ?? false}
      {...(onDone ? { onDone } : {})}
      doneMessage={t('account.password.done')}
      testId="account-password"
      username={username}
      canSubmit={current !== '' && isNewPasswordValid(next)}
      onSubmit={() =>
        AuthApi.changePassword({ current, next: next.password, nextConfirm: next.confirm })
      }
      onReset={() => {
        setCurrent('');
        setNext(EMPTY_NEW_PASSWORD);
      }}
    >
      {(errors) => (
        <>
          <PasswordField
            label={currentLabel ?? t('auth.current_password')}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            testId="account-password-current"
            error={errors.current}
            autoFocus
          />
          <NewPasswordFields
            value={next}
            onChange={setNext}
            testId="account-password-new"
            error={errors.password}
          />
        </>
      )}
    </DisclosureForm>
  );
}

// ─── PIN for kjøkkenskjermen ────────────────────────────────────────────────

function KitchenPinForm({ username }: { username: string | null }): ReactElement {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');

  // Formatfeilen vises først når man har gått videre til bekreftfeltet.
  const pinFormatError =
    pinConfirm !== '' && !PinSetBody.shape.pin.safeParse(pin).success
      ? t('account.pin.invalid')
      : null;
  const mismatch =
    pinConfirm !== '' && pin !== pinConfirm ? t('onboarding.step3.pin_mismatch') : null;

  return (
    <DisclosureForm
      title={t('account.pin.title')}
      body={t('account.pin.body')}
      rowLabel={t('account.pin.title')}
      submitLabel={t('account.pin.submit')}
      doneMessage={t('account.pin.done')}
      testId="account-pin"
      username={username}
      canSubmit={PinSetBody.safeParse({ password, pin, pinConfirm }).success}
      onSubmit={() => AuthApi.setPin({ password, pin, pinConfirm })}
      onReset={() => {
        setPassword('');
        setPin('');
        setPinConfirm('');
      }}
      onError={(e) => {
        // For enkel PIN: la passordet stå, tøm PIN-ene.
        if (e.code === 'forbidden_pin_pattern') {
          setPin('');
          setPinConfirm('');
        }
      }}
    >
      {(errors) => (
        <>
          <PasswordField
            label={t('auth.password')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            testId="account-pin-password"
            error={errors.current}
            autoFocus
          />
          <PinInput
            label={t('account.pin.new_pin')}
            value={pin}
            onChange={setPin}
            testId="account-pin-new"
            error={pinFormatError}
          />
          <PinInput
            label={t('account.pin.confirm_pin')}
            value={pinConfirm}
            onChange={setPinConfirm}
            testId="account-pin-confirm"
            error={mismatch}
          />
        </>
      )}
    </DisclosureForm>
  );
}

// ─── Logg ut ────────────────────────────────────────────────────────────────

/**
 * `variant="link"`: Meg-skissens røde «Logg ut» midt under kortene, uten
 * hint (#159, steg 9d). Standard er knappen med hint, som på /velg-passord.
 */
export function LogoutButton({
  variant = 'button',
}: { variant?: 'button' | 'link' } = {}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetMemberCache = useResetMemberCache();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function logout(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await AuthApi.logout();
    } catch (err) {
      const e = asApiError(err);
      // 401: økten er allerede borte — da er vi logget ut uansett.
      if (e.status !== 401) {
        setError(authErrorMessage(t, e));
        setPending(false);
        return;
      }
    }
    resetMemberCache('logged_out');
    navigate('/login', { replace: true });
  }

  const label = pending ? t('account.logging_out') : t('account.logout');
  return (
    <div className={variant === 'link' ? 'flex flex-col items-center gap-1' : 'space-y-2 pt-2'}>
      {variant === 'link' ? (
        <button
          type="button"
          onClick={() => void logout()}
          disabled={pending}
          data-testid="account-logout"
          className="flex min-h-11 items-center px-3 text-body-s font-extrabold text-bad disabled:opacity-60"
        >
          {label}
        </button>
      ) : (
        <>
          <p className="text-ink-2 text-sm">{t('account.logout_hint')}</p>
          <Button
            variant="secondary"
            onClick={() => void logout()}
            disabled={pending}
            data-testid="account-logout"
          >
            {label}
          </Button>
        </>
      )}
      {error ? (
        <p data-testid="account-logout-error" role="alert" className="text-bad text-sm">
          {error}
        </p>
      ) : null}
    </div>
  );
}
